import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getAllGroupingRelatedData } from 'app/app/(client)/utils/grouping-utils'
import { Building, BuildingStId, Equipment, EquipmentGroups, EquipmentGroupStId } from 'app/types/types'
import { DeleteGroupOption, EquipmentGroupAccordionProps, GroupOption } from 'app/types/ui/grouping'

const initialState = {
  searchQuery: '',
  isExpandAll: false,
  buildings: [] as Building[],
  equipmentGroups: {} as EquipmentGroups,
  groupOptions: [] as GroupOption[],
  selectedGroupForEquipment: {} as Record<NonNullable<EquipmentGroupStId>, string>,
  selectedGroupForBuilding: {} as Record<BuildingStId, EquipmentGroupStId[]>,
  rawEquipementByGroups: [] as EquipmentGroupAccordionProps[],
  rawEquipmentByLocations: [] as EquipmentGroupAccordionProps[],
  equipmentByGroups: [] as EquipmentGroupAccordionProps[],
  equipmentByLocations: [] as EquipmentGroupAccordionProps[],
  isOpenEquipmentDetailsModal: false,
  selectedEquipmentDetails: null as Equipment | null,
  isOnlyDefaultGroupPresent: false,
  deleteGroupOptions: [] as DeleteGroupOption[],
  defaultGroup: null as EquipmentGroupAccordionProps | null,
  isGroupDataInitialize: false
}

const groupingSlice = createSlice({
  name: 'grouping',
  initialState,
  reducers: {
    setGroupDataInitialize: (state, action: PayloadAction<boolean>) => {
      state.isGroupDataInitialize = action.payload
    },
    toggleExpandAll: state => {
      state.isExpandAll = !state.isExpandAll
    },

    setGroupData: (state, action: PayloadAction<{ newBuildings: Building[]; newEquipmentGroups: EquipmentGroups }>) => {
      const { newBuildings, newEquipmentGroups } = action.payload

      state.buildings = newBuildings
      state.equipmentGroups = newEquipmentGroups

      const {
        groupOptions,
        selectedGroupForBuilding,
        selectedGroupForEquipment,
        rawEquipementByGroups,
        rawEquipmentByLocations,
        equipmentByGroups,
        equipmentByLocations,
        isOnlyDefaultGroupPresent,
        deleteGroupOptions,
        defaultGroup
      } = getAllGroupingRelatedData(newBuildings, newEquipmentGroups, state.searchQuery)

      state.groupOptions = groupOptions
      state.selectedGroupForBuilding = selectedGroupForBuilding
      state.selectedGroupForEquipment = selectedGroupForEquipment
      state.rawEquipementByGroups = rawEquipementByGroups
      state.rawEquipmentByLocations = rawEquipmentByLocations
      state.equipmentByGroups = equipmentByGroups
      state.equipmentByLocations = equipmentByLocations
      state.isOnlyDefaultGroupPresent = isOnlyDefaultGroupPresent
      state.deleteGroupOptions = deleteGroupOptions
      state.defaultGroup = defaultGroup
    },
    openEquipmentDetailsModal: (state, action: PayloadAction<Equipment | null>) => {
      state.isOpenEquipmentDetailsModal = true
      state.selectedEquipmentDetails = action.payload
    },
    closeEquipmentDetailsModal: state => {
      state.isOpenEquipmentDetailsModal = false
      state.selectedEquipmentDetails = null
    },
    setEquipmentByGroupsAndLocations: (
      state,
      action: PayloadAction<{
        equipmentByGroups: EquipmentGroupAccordionProps[]
        equipmentByLocations: EquipmentGroupAccordionProps[]
      }>
    ) => {
      state.equipmentByGroups = action.payload.equipmentByGroups
      state.equipmentByLocations = action.payload.equipmentByLocations
    },
    resetEquipmentByGroupsAndLocations: state => {
      state.equipmentByGroups = state.rawEquipementByGroups
      state.equipmentByLocations = state.rawEquipmentByLocations
    },
    handleSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    }
  }
})

export const {
  setGroupDataInitialize,
  toggleExpandAll,
  openEquipmentDetailsModal,
  closeEquipmentDetailsModal,
  setGroupData,
  setEquipmentByGroupsAndLocations,
  resetEquipmentByGroupsAndLocations,
  handleSearchQuery
} = groupingSlice.actions
export default groupingSlice.reducer
